import * as Sentry from '@sentry/react';
import { BulkAssignmentOperation, BulkEmployeeData, BulkSagaState } from '@/api/apis/case';
import {
  BulkAssignmentResult,
  WsBulkAssignmentMessageContent,
  WsBulkAssignmentScenario,
} from '@/ws/notifiers/bulk-assignment/types';
import { ParsedBulkAssignmentMessage } from '../types';

const { logger } = Sentry;

const SAGA_STATE_RANK: Record<BulkSagaState, number> = {
  [BulkSagaState.Pending]: 0,
  [BulkSagaState.InProgress]: 1,
  [BulkSagaState.Completed]: 2,
  [BulkSagaState.Failed]: 2,
};

const TERMINAL_RANK = 2;

export const isTerminalOperation = (operation: BulkAssignmentOperation) =>
  SAGA_STATE_RANK[operation.status] === TERMINAL_RANK;

export const getOperationKey = (operation: BulkAssignmentOperation) => operation.operationId || operation.caseId;

/**
 * Keeps the higher-ranked status for each operation, so a stale or duplicated message can never
 * rewind one — every message carries the full operation set, so a delayed snapshot will report an
 * already-completed operation as still in progress. Since statuses only ever advance, keeping the
 * highest one seen converges on the same result whatever order the messages arrive in.
 */
export const mergeOperations = (
  previous: Record<string, BulkAssignmentOperation>,
  incoming: BulkAssignmentOperation[],
): Record<string, BulkAssignmentOperation> =>
  incoming.reduce(
    (accumulator, operation) => {
      const key = getOperationKey(operation);
      const existing = accumulator[key];

      if (existing && SAGA_STATE_RANK[existing.status] >= SAGA_STATE_RANK[operation.status]) return accumulator;

      return { ...accumulator, [key]: operation };
    },
    { ...previous },
  );

export const safeJsonParse = <T>(raw: string | undefined, fallback: T): T => {
  if (!raw) return fallback;

  try {
    return JSON.parse(raw) as T;
  } catch {
    logger.warn('Failed to parse bulk assignment websocket payload');
    return fallback;
  }
};

export const parseBulkAssignmentMessage = (data: WsBulkAssignmentMessageContent): ParsedBulkAssignmentMessage => ({
  correlationId: data.correlationId,
  isCompleted: data.scenario === WsBulkAssignmentScenario.Completed,
  operations: safeJsonParse<BulkAssignmentResult[]>(data.results, [])
    .map((result) => result.operation)
    .filter(Boolean),
  relatedEmployees: safeJsonParse<BulkEmployeeData[]>(data.relatedEmployees, []),
});
